/**
 * 평가기준 상세 표시 렌더 — 구 SMS(RiskAssessmentOverviewStep) 정본을 추출한 공용 컴포넌트.
 *
 * web(apps/web/components/reports/RiskCriteriaDisplay.tsx)에서 rc 패키지로 이동(2026-07-19,
 * 결정 2026-07-07-composer-template-variables 개정 5 — WYSIWYG: admin 캔버스·web 실폼이 같은
 * 등록형 위젯(risk-criteria-detail)으로 동형 렌더).
 *
 * 소비처:
 *  - 구 SMS 작성 스텝(RiskAssessmentOverviewStep의 "평가기준 보기" 접이식 — web이 rc에서 import)
 *  - composer 등록형 위젯 `risk-criteria-detail`(RiskCriteriaDetailWidget — web/admin 공용)
 *
 * props는 렌더에 실제 필요한 기준 배열만으로 좁혀(구 RiskAssessmentConfig 통짜 대신) 소비처가
 * 각자의 소스(전체 config / composer criteria 합성 객체)로 구조 호환 전달한다. JSX는 구 SMS 동형 유지.
 *
 * 등급색(hex 유래 배경/텍스트/보더)은 **인라인 style** — rc에서만 처음 쓰는 Tailwind 색 유틸은
 * 앱 CSS에 미생성될 수 있어 조용히 무스타일이 된다(rc 신규 Tailwind 유틸 금지 —
 * 메모리 rc-package-tailwind-class-purge-inline-style). 구 web 구현의 hexToTailwindStyle(클래스
 * 매핑)을 같은 톤의 hex 값 인라인으로 전환(시각 동일). 레이아웃·회색 표 클래스(text-sm/border 등)는
 * web/admin 공통 유틸이라 유지.
 */
import type { CSSProperties } from 'react';
import { getManagementAction } from '@safetyhub/shared';
import { hexToRgba } from './resolver';

/** 빈도(가능성)/강도(중대성) 수준 항목 */
export interface CriteriaLevelItem {
  level: number;
  label: string;
  description?: string;
}

/** 위험성 수준 판정(점수 구간) 항목 */
export interface RiskLevelCriteriaItem {
  min: number;
  max: number;
  label: string;
  managementAction?: string;
  /** @deprecated action 대신 managementAction 사용 */
  action?: string;
  color?: string;
}

/** 상중하(3단계) 수준 항목 */
export interface LevelCriteriaItem extends CriteriaLevelItem {
  managementAction?: string;
  /** @deprecated action 대신 managementAction 사용 */
  action?: string;
  color?: string;
}

/** FrequencySeverityDisplay가 소비하는 기준 3종 */
export interface FrequencySeverityCriteriaData {
  likelihoodCriteria: CriteriaLevelItem[];
  severityCriteria: CriteriaLevelItem[];
  riskLevelCriteria: RiskLevelCriteriaItem[];
}

/**
 * 등급색 계열 톤 — Tailwind v3 팔레트 hex를 인라인로 고정(구 web 클래스 매핑과 동일 시각).
 * bg50=연한 행 배경 / bg100·text700=매트릭스 강조 셀 / border500=상중하 카드 좌보더.
 */
interface ColorFamilyTones {
  bg50: string;
  bg100: string;
  text700: string;
  border500: string;
}

const COLOR_FAMILY_TONES: Record<string, ColorFamilyTones> = {
  red: { bg50: '#fef2f2', bg100: '#fee2e2', text700: '#b91c1c', border500: '#ef4444' },
  orange: { bg50: '#fff7ed', bg100: '#ffedd5', text700: '#c2410c', border500: '#f97316' },
  yellow: { bg50: '#fefce8', bg100: '#fef9c3', text700: '#a16207', border500: '#eab308' },
  lime: { bg50: '#f7fee7', bg100: '#ecfccb', text700: '#4d7c0f', border500: '#84cc16' },
  green: { bg50: '#f0fdf4', bg100: '#dcfce7', text700: '#15803d', border500: '#22c55e' },
  emerald: { bg50: '#ecfdf5', bg100: '#d1fae5', text700: '#047857', border500: '#10b981' },
  blue: { bg50: '#eff6ff', bg100: '#dbeafe', text700: '#1d4ed8', border500: '#3b82f6' },
  gray: { bg50: '#f9fafb', bg100: '#f3f4f6', text700: '#374151', border500: '#6b7280' },
};

/** criteria.color hex → 색 계열 정규화(구 web getRowBgClass/hexToTailwindStyle의 hex 목록 동일). */
const HEX_TO_FAMILY: Record<string, string> = {
  '#ef4444': 'red',
  '#dc2626': 'red',
  '#b91c1c': 'red',
  '#f97316': 'orange',
  '#ea580c': 'orange',
  '#eab308': 'yellow',
  '#ca8a04': 'yellow',
  '#f59e0b': 'yellow',
  '#d97706': 'yellow',
  '#84cc16': 'lime',
  '#22c55e': 'green',
  '#16a34a': 'green',
  '#15803d': 'green',
  '#10b981': 'emerald',
  '#3b82f6': 'blue',
  '#6b7280': 'gray',
};

function familyTones(color: string | undefined): ColorFamilyTones | undefined {
  if (!color) return undefined;
  const family = HEX_TO_FAMILY[color.toLowerCase().trim()];
  return family ? COLOR_FAMILY_TONES[family] : undefined;
}

/** criteria.color hex → 테이블 행 배경(50단계 tint) 인라인 style — 구 getRowBgClass 동형. */
function rowBgStyle(color: string | undefined, label: string): CSSProperties {
  const tones = familyTones(color);
  if (tones) return { backgroundColor: tones.bg50 };
  // label fallback (legacy)
  if (label === '높음') return { backgroundColor: COLOR_FAMILY_TONES.red.bg50 };
  if (label === '보통') return { backgroundColor: COLOR_FAMILY_TONES.yellow.bg50 };
  return { backgroundColor: COLOR_FAMILY_TONES.green.bg50 };
}

/** FREQUENCY_SEVERITY: 매트릭스 + 위험성 수준 + 레벨 설명 */
export function FrequencySeverityDisplay({ config }: { config: FrequencySeverityCriteriaData }) {
  const likelihood = [...config.likelihoodCriteria].sort((a, b) => b.level - a.level);
  const severity = [...config.severityCriteria].sort((a, b) => a.level - b.level);
  const riskLevels = [...config.riskLevelCriteria].sort((a, b) => b.min - a.min);

  const getRiskLevel = (score: number) => {
    return riskLevels.find((r) => score >= r.min && score <= r.max);
  };

  // 매트릭스 셀 등급색 — 1순위 criteria.color hex 계열(bg100+text700), 미매칭 hex는 rgba tint
  // 폴백(구 hexToInlineStyle 동형), 2순위 라벨(legacy), 그 외 gray. 전부 인라인(rc Tailwind 규약).
  const riskCellStyle = (score: number): CSSProperties => {
    const level = getRiskLevel(score);
    if (!level) return { backgroundColor: COLOR_FAMILY_TONES.gray.bg100, color: COLOR_FAMILY_TONES.gray.text700 };
    const tones = familyTones(level.color);
    if (tones) return { backgroundColor: tones.bg100, color: tones.text700 };
    if (level.color && /^#[0-9a-f]{6}$/i.test(level.color)) {
      // 변환은 resolver hexToRgba(rc 정본) — 인라인 parseInt 사본 금지.
      return { backgroundColor: hexToRgba(level.color, 0.15), color: level.color };
    }
    // hardcoded label fallback (legacy) — 구 클래스(text-*-800) 대응 톤.
    if (level.label === '높음')
      return { backgroundColor: COLOR_FAMILY_TONES.red.bg100, color: '#991b1b', fontWeight: 600 };
    if (level.label === '보통')
      return { backgroundColor: COLOR_FAMILY_TONES.yellow.bg100, color: '#854d0e' };
    if (level.label === '낮음')
      return { backgroundColor: COLOR_FAMILY_TONES.green.bg100, color: '#166534' };
    return { backgroundColor: COLOR_FAMILY_TONES.gray.bg100, color: '#1f2937' };
  };

  return (
    <div className="space-y-4">
      {/* 매트릭스 */}
      <div>
        <h5 className="text-sm font-medium text-gray-700 mb-2">위험성 매트릭스 (가능성 x 중대성)</h5>
        <div className="overflow-x-auto">
          <table className="border-collapse text-sm w-full max-w-md">
            <thead>
              <tr>
                <th className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-xs text-gray-500">
                  가능성 \ 중대성
                </th>
                {severity.map((s) => (
                  <th key={s.level} className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-center text-xs">
                    {s.label} ({s.level})
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {likelihood.map((l) => (
                <tr key={l.level}>
                  <th className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-left text-xs font-medium">
                    {l.label} ({l.level})
                  </th>
                  {severity.map((s) => {
                    const score = l.level * s.level;
                    return (
                      <td
                        key={s.level}
                        className="border border-gray-300 px-2 py-1.5 text-center text-xs"
                        style={riskCellStyle(score)}
                      >
                        {score}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 위험성 수준 관리기준 */}
      <div>
        <h5 className="text-sm font-medium text-gray-700 mb-2">위험성 수준 관리기준</h5>
        <div className="overflow-x-auto">
          <table className="border-collapse text-xs w-full max-w-lg">
            <thead>
              <tr>
                <th className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-left">위험성 수준</th>
                <th className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-left">관리기준</th>
                <th className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-left">내용</th>
              </tr>
            </thead>
            <tbody>
              {riskLevels.map((r) => {
                const ma = r.managementAction ? getManagementAction(r.managementAction) : undefined;
                return (
                  <tr key={r.label} style={rowBgStyle(r.color, r.label)}>
                    <td className="border border-gray-300 px-2 py-1.5 font-semibold">
                      {r.label} ({r.min}~{r.max})
                    </td>
                    <td className="border border-gray-300 px-2 py-1.5 font-medium">
                      {ma?.label || r.action || '-'}
                    </td>
                    <td className="border border-gray-300 px-2 py-1.5 text-gray-600">
                      {ma?.description || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 가능성/중대성 레벨 설명 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-1.5">가능성(빈도) 기준</h5>
          <div className="space-y-1">
            {likelihood.map((l) => (
              <div key={l.level} className="flex items-start gap-2 text-xs">
                <span className="font-medium text-gray-700 whitespace-nowrap">{l.label} ({l.level})</span>
                <span className="text-gray-500">{l.description}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-1.5">중대성(강도) 기준</h5>
          <div className="space-y-1">
            {[...config.severityCriteria].sort((a, b) => b.level - a.level).map((s) => (
              <div key={s.level} className="flex items-start gap-2 text-xs">
                <span className="font-medium text-gray-700 whitespace-nowrap">{s.label} ({s.level})</span>
                <span className="text-gray-500">{s.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** THREE_LEVEL: 레벨 목록 (상/중/하) */
export function ThreeLevelDisplay({ config }: { config: { levelCriteria?: LevelCriteriaItem[] } }) {
  const levels = [...(config.levelCriteria || [])].sort((a, b) => b.level - a.level);

  if (levels.length === 0) return <p className="text-sm text-gray-400">레벨 기준이 없습니다.</p>;

  return (
    <div>
      <h5 className="text-sm font-medium text-gray-700 mb-2">위험성 수준 관리기준</h5>
      <div className="space-y-2">
        {levels.map((l) => {
          const ma = l.managementAction ? getManagementAction(l.managementAction) : undefined;
          // 좌보더 색 — color 계열(500단계), 미매칭/미지정은 라벨 fallback(상=red/중=yellow/하=green).
          const tones = familyTones(l.color);
          const borderLeftColor = tones
            ? tones.border500
            : l.color
              ? COLOR_FAMILY_TONES.gray.border500
              : l.label === '상'
                ? COLOR_FAMILY_TONES.red.border500
                : l.label === '중'
                  ? COLOR_FAMILY_TONES.yellow.border500
                  : COLOR_FAMILY_TONES.green.border500;
          return (
            <div
              key={l.level}
              className="px-3 py-2"
              // 좌보더 4px + 우측 라운드는 카드 구조 임계 시각 — admin CSS에 border-l-4/rounded-r-md
              // 미생성 가능성이 있어 인라인 고정(rc Tailwind 규약).
              style={{
                borderLeft: `4px solid ${borderLeftColor}`,
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
                ...rowBgStyle(l.color, l.label),
              }}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{l.label}</span>
                <span className="text-xs text-gray-500">(Level {l.level})</span>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">{l.description}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {ma ? `${ma.label}: ${ma.description}` : l.action || '-'}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
